import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Dealer } from '../types';
import api, { clearAuthHeader } from '../utils/api';
import { getPermissionSettings, getUserRole, type Role } from '../utils/permissions';

// SW push path — versioned via global set by force-react.php, fallback to unversioned
const PUSH_SW_PATH = () => {
  const v = (typeof window !== 'undefined' && (window as any).BOAT_TRADER_API?.version)
    ? `?ts=${(window as any).BOAT_TRADER_API.version}`
    : '';
  return `/wp-content/plugins/boat-trader-v3/sw-push.js${v}`;
};

async function getPushRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing && existing.active && existing.active.scriptURL.includes('sw-push')) {
    return existing;
  }
  try {
    return await navigator.serviceWorker.register(PUSH_SW_PATH());
  } catch {
    if (existing) return existing;
    throw new Error('SW registration failed');
  }
}

// Track last posted subscription endpoint to avoid duplicate POSTs
let lastPostedEndpoint = '';

const requestPushPermissionIfNeeded = async () => {
  if (!('Notification' in window) || !('PushManager' in window)) return;
  if (Notification.permission === 'denied') return;
  if (Notification.permission === 'default') {
    const granted = await Notification.requestPermission();
    if (granted !== 'granted') return;
  }

  try {
    const registration = await getPushRegistration();
    const existingSub = await registration.pushManager.getSubscription();
    let sub: PushSubscription | null = existingSub;
    let vapidKey = '';
    if (!existingSub) {
      const response = await api.get('/push/vapid-key');
      vapidKey = response.data.data?.publicKey;
      if (!vapidKey) return;
      sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });
    } else {
      const response = await api.get('/push/vapid-key');
      vapidKey = response.data.data?.publicKey || '';
    }
    if (!sub) return;
    const endpoint = sub.toJSON().endpoint as string;
    if (endpoint !== lastPostedEndpoint) {
      await api.post('/push/subscribe', { subscription: sub.toJSON() });
      lastPostedEndpoint = endpoint;
    }
    // Get FCM token via Firebase SDK (lazy dynamic import)
    if (vapidKey) {
      try {
        const { getFcmToken, registerFcmToken } = await import('../lib/firebase')
        const fcmToken = await getFcmToken(vapidKey)
        if (fcmToken) {
          await registerFcmToken(fcmToken, endpoint)
        }
      } catch (e) {
        console.warn('FCM registration skipped:', e)
      }
    }
    } catch {
    // Silent fail
  }
};

function urlBase64ToUint8Array(base64String: string): BufferSource {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
}

interface AuthContextType {
  user: Dealer | null;
  token: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  setAuth: (user: Dealer, token: string) => void;
  logout: () => Promise<void>;
}

const noop = async () => {};

const defaultContext: AuthContextType = {
  user: null,
  token: null,
  isAuthenticated: false,
  isInitialized: false,
  setAuth: noop,
  logout: noop,
};

const AuthContext = createContext<AuthContextType>(defaultContext);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<Dealer | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const setAuthCallback = useCallback(async (newUser: any, newToken: string) => {
    setUser(newUser);
    setToken(newToken);

    if (typeof window !== 'undefined') {
      (window as any).boat_trader_token = newToken;
      (window as any).user = newUser;
      const dealerId = newUser?.dealer_id;
      if (dealerId) {
        (window as any).dealer_id = dealerId;
      }
      try {
        localStorage.setItem('boat_trader_token', newToken);
      } catch (e) {
      }
    }

    try {
      const sessionData = (window as any).boat_trader_session;
      const sessionObj = sessionData ? (typeof sessionData === 'string' ? JSON.parse(sessionData) : sessionData) : null;

      if (sessionObj?.session_id) {
        const deviceInfo = {
          device_type: /mobile|android|iphone/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
          browser: /Chrome/i.test(navigator.userAgent) ? 'Chrome' : /Firefox/i.test(navigator.userAgent) ? 'Firefox' : 'Other',
          os: /Windows/i.test(navigator.userAgent) ? 'Windows' : /Mac/i.test(navigator.userAgent) ? 'Mac' : /Linux/i.test(navigator.userAgent) ? 'Linux' : 'Other',
        };

        await api.post('/track/session/login', {
          session_id: sessionObj.session_id,
          user_id: newUser?.id || newUser?.user_id,
          user_type: 'dealer',
          device_type: deviceInfo.device_type,
          browser: deviceInfo.browser,
          os: deviceInfo.os,
          login_at: new Date().toISOString(),
        });
      }
    } catch (e) {
      console.error('Login tracking failed:', e);
    }
  }, []);

  const logoutCallback = useCallback(async () => {
    // Track logout before clearing everything
    try {
      const sessionData = (window as any).boat_trader_session;
      const sessionObj = sessionData ? (typeof sessionData === 'string' ? JSON.parse(sessionData) : sessionData) : null;
      const pagesViewed = sessionObj?.pages_viewed || 0;
      const sessionId = sessionObj?.session_id || null;
      
      if (sessionId) {
        await api.post('/track/session/logout', {
          session_id: sessionId,
          logout_at: new Date().toISOString(),
          pages_viewed: pagesViewed || 0,
          is_bounce: (pagesViewed || 0) <= 1,
        });
      }
    } catch (e) {
      // Ignore tracking errors, continue with logout
    }
    
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Continue with local logout even if server call fails
    }
    
    setUser(null);
    setToken(null);
    clearAuthHeader();
    
    // Clear any global state
    if (typeof window !== 'undefined') {
      delete (window as any).boat_trader_token;
      delete (window as any).user;
      delete (window as any).dealer_id;
      delete (window as any).boat_trader_session;
      localStorage.removeItem('boat_trader_token');
    }
  }, []);

  // Auth initialization effect - restores token from localStorage for dealers
  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsInitialized(true);
      return;
    }
    
    const wpToken = (window as any).BOAT_TRADER_API?.wp_session_token;
    const boatTraderApi = (window as any).BOAT_TRADER_API;
    const isWpLoggedIn = boatTraderApi?.is_logged_in === true || boatTraderApi?.is_logged_in === '1' || boatTraderApi?.is_logged_in === 1;
    const wpUser = boatTraderApi?.user;
    
    if (isWpLoggedIn && wpUser) {
      // Check if WP user is admin
      const isAdmin = boatTraderApi?.is_admin === true ||
        boatTraderApi?.is_admin === '1' ||
        wpUser?.is_admin === true ||
        wpUser?.is_admin === '1' ||
        wpUser?.roles?.includes?.('administrator') ||
        wpUser?.caps?.manage_options === true;
      
      if (!isAdmin) {
        // Non-admin (dealer): try to restore token from localStorage on page refresh
        // If the user is logged into WP and has a stored dealer token, restore it
        const storedToken = localStorage.getItem('boat_trader_token');
        if (storedToken && wpUser) {
          // Reconstruct dealer user from WP user data
          const dealerUser: Dealer = {
            id: wpUser.id,
            email: wpUser.email,
            name: wpUser.name,
            company_name: wpUser.company_name || wpUser.name || '',
            lat: wpUser.lat || null,
            lng: wpUser.lng || null,
            city_id: wpUser.city_id || 1,
            city: wpUser.city || wpUser.citta || '',
            citta: wpUser.citta || wpUser.city || '',
            logo: wpUser.logo || '',
            phone: wpUser.phone || '',
            address: wpUser.address || '',
            provincia: wpUser.provincia || '',
            regione: wpUser.regione || '',
            is_admin: false,
          };
          // Restore token to global window so API interceptor picks it up
          (window as any).boat_trader_token = storedToken;
          (window as any).user = dealerUser;
          setAuthCallback(dealerUser, storedToken);
        }
        setIsInitialized(true);
        return;
      }
      
      // For admin: create a fake user object with dealer-like fields (admin uses wp_admin as dealer)
      const hasValidCoords = wpUser.lat != null && wpUser.lng != null && wpUser.lat !== 0 && wpUser.lng !== 0;
      const adminUser: Dealer = {
        id: wpUser.id,
        name: wpUser.name || wpUser.display_name,
        email: wpUser.email,
        company_name: wpUser.company_name || wpUser.name || 'Admin User',
        lat: hasValidCoords ? wpUser.lat : (isAdmin ? 40.3516 : null),
        lng: hasValidCoords ? wpUser.lng : (isAdmin ? 18.1723 : null),
        admin_lat: isAdmin ? (wpUser.admin_lat || 40.3516) : null,
        admin_lng: isAdmin ? (wpUser.admin_lng || 18.1723) : null,
        city_id: 1,
        city: hasValidCoords ? (wpUser.city || wpUser.citta || '') : '',
        citta: hasValidCoords ? (wpUser.city || wpUser.citta || '') : '',
        logo: wpUser.logo || '',
        phone: wpUser.phone || '',
        address: wpUser.address || '',
        provincia: 'LE',
        regione: 'Puglia',
        created_at: new Date().toISOString(),
        is_admin: isAdmin,
      };

      // Use real token from PHP (generated server-side) instead of fake wp_session_
      const adminToken = wpUser.token || 'wp_session_' + wpUser.id;
      
      // Store in global window object instead of localStorage
      (window as any).boat_trader_token = adminToken;
      (window as any).user = adminUser;

      // Persist token to localStorage for page reload resilience
      try {
        localStorage.setItem('boat_trader_token', adminToken);
      } catch (e) {
        // localStorage may be unavailable in some environments
      }

      // Batch update using setAuthCallback
      setAuthCallback(adminUser, adminToken);
    } else {
      // Fallback: try to restore session from localStorage token.
      // This handles the case where the WP cookie session is unavailable
      // (e.g. LiteSpeed cache serves stale HTML, cookie expired, etc.)
      // but a valid boat_trader_token still exists in localStorage.
      const storedToken = localStorage.getItem('boat_trader_token');
      if (storedToken) {
        (window as any).boat_trader_token = storedToken;
        setToken(storedToken);
        // Validate token and fetch full user via /auth/me (works with token header alone,
        // doesn't need WP cookie — check_auth validates X-Boat-Token first).
        // Keep isInitialized=false during the async call to avoid flash of broken UI.
        api.get('/auth/me').then(resp => {
          const userData = resp.data?.data?.user;
          if (userData) {
            setAuthCallback(userData, storedToken);
          } else {
            localStorage.removeItem('boat_trader_token');
            delete (window as any).boat_trader_token;
            setToken(null);
          }
          setIsInitialized(true);
        }).catch(() => {
          localStorage.removeItem('boat_trader_token');
          delete (window as any).boat_trader_token;
          setToken(null);
          setIsInitialized(true);
        });
        return; // exit — setIsInitialized(true) called after async completes
      }
    }
    setIsInitialized(true);
  }, [setAuthCallback]);

  // Register push notifications when authenticated
  useEffect(() => {
    if (isInitialized && token) {
      requestPushPermissionIfNeeded().catch(() => {});
    }
  }, [isInitialized, token]);

  const contextValue: AuthContextType = React.useMemo(() => {
    // Authentication requires a valid token (JWT or wp_session). WordPress login alone (cookies) is not enough.
    const hasToken = isInitialized && !!token;
    const authenticated = hasToken;
    
    return {
      user,
      token,
      isAuthenticated: authenticated,
      isInitialized,
      setAuth: setAuthCallback,
      logout: logoutCallback,
    };
  }, [user, token, isInitialized, setAuthCallback, logoutCallback]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  return useContext(AuthContext);
}

export function useUser(): Dealer | null {
  return useContext(AuthContext).user;
}

export function useAuthReady(): boolean {
  // Returns true once auth state is determined.
  // Queries use this as `enabled` guard — they handle public vs authenticated internally.
  const context = useContext(AuthContext);
  return context.isInitialized;
}

export function useToken(): string | null {
  return useContext(AuthContext).token;
}

export function useIsAuthenticated(): boolean {
  return useContext(AuthContext).isAuthenticated;
}

// Get dealer ID from in-memory context only (no localStorage)
export function getDealerIdFromStorage(): number | null {
  if (typeof window === 'undefined') return null;
  const dealerId = (window as any).dealer_id;
  return dealerId ? parseInt(dealerId, 10) : null;
}

export function useDealerId(): number | null {
  const { user } = useContext(AuthContext);
  if (user) {
    return user.user_id || user.id || getDealerIdFromStorage();
  }
  return getDealerIdFromStorage();
}

export function useCanEditItem(itemDealerId: number | undefined): boolean {
  const { user } = useContext(AuthContext);
  
  if (!user) return false;
  
  if (user.is_admin) return true;
  
  const dealerId = (user as any).dealer_id || (user as any).user_id;
  if (itemDealerId !== undefined && dealerId === itemDealerId) return true;
  
  return false;
}

export function useCanReserve(itemDealerId: number | undefined): boolean {
  const { user } = useContext(AuthContext);
  const settings = getPermissionSettings();
  const role = getUserRole(user) as Role;
  
  if (!user) return true;
  
  if (role === 'admin') return true;
  
  if (role === 'dealer') {
    if (!settings.dealer_can_prenota) return false;
    const userId = (user as any).dealer_id || (user as any).user_id;
    if (itemDealerId !== undefined && userId !== itemDealerId) return true;
    return false;
  }
  
  return true;
}

export function useCanTrade(itemDealerId: number | undefined): boolean {
  const { user } = useContext(AuthContext);
  const settings = getPermissionSettings();
  const role = getUserRole(user) as Role;
  
  if (!user) return false;
  
  if (role === 'admin') return true;
  
  if (role === 'dealer') {
    if (!settings.dealer_can_trattativa) return false;
    const userId = (user as any).dealer_id || (user as any).user_id;
    if (itemDealerId !== undefined && userId !== itemDealerId) return true;
    return false;
  }
  
  return false;
}

export function useCanAccessAdmin(): boolean {
  const { user } = useContext(AuthContext);
  
  if (!user) return false;
  
  return user.is_admin === true;
}

export function useCanDeleteItem(): boolean {
  const { user } = useContext(AuthContext);
  const settings = getPermissionSettings();
  const role = getUserRole(user) as Role;
  
  if (!user) return false;
  
  if (role === 'admin') return true;
  
  if (role === 'dealer') return settings.dealer_can_delete ?? false;
  
  return false;
}

export type PermissionAction = 
  | 'create' 
  | 'read' 
  | 'update' 
  | 'delete' 
  | 'reserve' 
  | 'cancel_reservation' 
  | 'chat' 
  | 'make_offer' 
  | 'access_admin';

export interface PermissionParams {
  action: PermissionAction;
  itemDealerId?: number;
  itemStatus?: string;
}

export function usePermissions() {
  const { user } = useContext(AuthContext);
  
  const getUserId = (): number | undefined => {
    if (!user) return undefined;
    return user.id || (user as any).user_id;
  };
  
  const isAdmin = user?.is_admin === true;
  const userId = getUserId();
  
  const checkPermission = useCallback((params: PermissionParams): boolean => {
    const { action, itemDealerId, itemStatus } = params;
    
    if (!user) return false;
    
    if (isAdmin) return true;
    
    const currentUserId = getUserId();
    const isOwner = itemDealerId !== undefined && currentUserId === itemDealerId;
    const isOtherDealer = itemDealerId !== undefined && currentUserId !== itemDealerId;
    
    switch (action) {
      case 'create':
        return true;
      case 'read':
        return true;
      case 'update':
        if (isOwner && itemStatus?.toLowerCase() === 'sold') {
          return true;
        }
        return false;
      case 'delete':
        return false;
      case 'reserve':
        return isOtherDealer;
      case 'cancel_reservation':
        return isOwner;
      case 'chat':
        return isOtherDealer;
      case 'make_offer':
        return isOtherDealer;
      case 'access_admin':
        return false;
      default:
        return false;
    }
  }, [user, isAdmin, userId]);
  
  const canCreate = useCallback(() => checkPermission({ action: 'create' }), [checkPermission]);
  const canRead = useCallback((itemDealerId?: number) => checkPermission({ action: 'read', itemDealerId }), [checkPermission]);
  const canUpdate = useCallback((itemDealerId: number | undefined, itemStatus?: string) => 
    checkPermission({ action: 'update', itemDealerId, itemStatus }), [checkPermission]);
  const canDelete = useCallback(() => checkPermission({ action: 'delete' }), [checkPermission]);
  const canReserve = useCallback((itemDealerId: number | undefined) => 
    checkPermission({ action: 'reserve', itemDealerId }), [checkPermission]);
  const canCancelReservation = useCallback((reservationDealerId: number | undefined) => 
    checkPermission({ action: 'cancel_reservation', itemDealerId: reservationDealerId }), [checkPermission]);
  const canChat = useCallback((otherDealerId: number | undefined) => 
    checkPermission({ action: 'chat', itemDealerId: otherDealerId }), [checkPermission]);
  const canMakeOffer = useCallback((itemDealerId: number | undefined) => 
    checkPermission({ action: 'make_offer', itemDealerId }), [checkPermission]);
  const canAccessAdmin = useCallback(() => checkPermission({ action: 'access_admin' }), [checkPermission]);
  
  const isCurrentUserAdmin = useCallback(() => isAdmin, [isAdmin]);
  
  const isOwner = useCallback((itemDealerId: number | undefined): boolean => {
    if (!user || itemDealerId === undefined) return false;
    const currentUserId = getUserId();
    return currentUserId === itemDealerId;
  }, [user]);
  
  return {
    checkPermission,
    canCreate,
    canRead,
    canUpdate,
    canDelete,
    canReserve,
    canCancelReservation,
    canChat,
    canMakeOffer,
    canAccessAdmin,
    isCurrentUserAdmin,
    isOwner,
    isAdmin,
  };
}
